import { filter, finalize, map, take, tap } from 'rxjs/operators';
import { Inject, Injectable, NgZone, Optional } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { BehaviorSubject, interval } from 'rxjs';
import { HttpHandlerService } from '@shared/services/http-handler.service';
import { AuthenticationService } from '@app/authentication/authentication.service';
import { environment } from '@env';

// Mesma chave do zpro-widget.js (script estatico). A "_v2" forca um webchatId novo pra quem ja tinha sessao: a ZPRO
// so atualiza o name de um contato existente e mantem o e-mail antigo, entao o contato precisa ser novo.
// Se mudar aqui, mudar tambem no widget e subir o numero do ?v= do script no index.html.
const WEBCHAT_ID_STORAGE_KEY = 'channelWebchatId_v2';

@Injectable({ providedIn: 'root' })
export class ZproService {

  private widget: HTMLElement;
  private button: HTMLElement;
  private closeBtn: HTMLElement;
  private container: HTMLElement;

  // Quantidade de mensagens novas do chat (bot ou atendente) que o cliente ainda não viu, informada pelo widget. O botão
  // "Peça ajuda de um especialista" mostra esse número.
  public readonly naoLidas$ = new BehaviorSubject<number>(0);

  constructor(
    @Inject(DOCUMENT) private doc: Document,
    private http: HttpHandlerService,
    private authentication: AuthenticationService,
    @Optional() private zone?: NgZone,
  ) {
    // O widget (script estático, fora do Angular) avisa aqui quando a quantidade muda. Como a chamada vem de fora, entra
    // na zona do Angular pra tela atualizar. Enquanto esta função existir, o widget não mostra o próprio botão flutuante.
    (window as any).__ZPRO_NAO_LIDAS__ = (quantidade: number) => {
      const atualizar = () => this.naoLidas$.next(Number(quantidade) || 0);
      if (this.zone) {
        this.zone.run(atualizar);
      } else {
        atualizar();
      }
    };
    // Ponte pro widget (script estático) poder pedir um token novo sob demanda —
    // usada na troca de sessão ("Nova sessão") e na renovação por expiração,
    // casos em que o webchatId muda e o token anterior deixa de ser válido pra ele.
    // rxjs 6 nesse repo nao tem firstValueFrom (so' a partir da 7) - toPromise() e' o
    // equivalente compativel aqui, mesmo padrao ja usado em outras partes do projeto.
    (window as any).__ZPRO_REREGISTER__ = () => this.registerSession().toPromise();
    // Token de um webchatId específico (a conversa já finalizada, pra enviar a cópia por e-mail depois do refresh
    // automático), sem mexer no id nem nos globais da conversa atual.
    (window as any).__ZPRO_TOKEN_PARA__ = (webchatId: string) => this.registerTokenFor(webchatId).toPromise();
  }

  // Registra a sessão do webchat através da API do Integrador, que fala com a ZPRO
  // server-to-server (guardando a server key só no backend) e devolve só o token de
  // sessão. Esse token (e o necessário pra chamar nossa própria API depois — base da
  // URL e header de autorização) fica exposto em window pro widget consumir, já que
  // o widget é um script estático, sem acesso ao environment/HttpHandlerService.
  public registerSession() {
    const webchatId = this.getOrCreateWebchatId();
    (window as any).__ZPRO_API_BASE__ = environment.serviceUrl;
    (window as any).__ZPRO_AUTH_HEADER__ = this.authentication.getAuthorizationHeaders().get('Authorization');
    return this.requestRegister(webchatId)
      .pipe(tap(result => {
        (window as any).__ZPRO_TOKEN__ = result.token;
        // Canal ZPRO do ambiente, usado pelo widget no envio de midia (que vai direto pra ZPRO).
        if (result.wabaId) { (window as any).__ZPRO_WABA_ID__ = result.wabaId; }
      }));
  }

  // So o token de um webchatId informado (nao grava o id nem os globais da conversa atual)
  public registerTokenFor(webchatId: string) {
    return this.requestRegister(webchatId).pipe(map(result => result.token));
  }

  private requestRegister(webchatId: string) {
    return this.http.request<{ token: string; webchatId: string; wabaId?: string }>(`${environment.serviceUrl}/api/zpro/webchat/register`)
      .setBody({ webchatId })
      .post();
  }

  // localStorage (nao sessionStorage) de proposito - sobrevive a fechar a aba/navegador,
  // pra nao perder a conversa se o cliente fechar sem querer enquanto espera resposta.
  private getOrCreateWebchatId(): string {
    let id = localStorage.getItem(WEBCHAT_ID_STORAGE_KEY);
    if (!id) {
      id = `${Date.now().toString(36)}-${Math.random().toString(36).substring(2, 8)}`;
      localStorage.setItem(WEBCHAT_ID_STORAGE_KEY, id);
    }
    return id;
  }

  public init() {
    return interval(100).pipe(
      filter(() => {
        try {
          this.widget = this.doc.getElementById('channel-chat-widget');
          this.button = this.doc.getElementById('channel-chat-button');
          this.closeBtn = this.doc.getElementById('channel-chat-close');
          this.container = this.doc.getElementById('channel-chat-container');

          return this.isReady;
        } catch (e) {
          return false;
        }
      }),
      take(1),
      finalize(() => {
        this.widget.style.display = 'none';
      })
    );
  }

  public toggleChat() {
    if (this.isReady) {
      const isOpen = this.container.classList.contains('show');
      (isOpen ? this.closeBtn : this.button).click();
      return true;
    }
    return false;
  }

  public get isReady() {
    return !!this.widget && !!this.button && !!this.closeBtn && !!this.container;
  }

}
